import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { BrainCircuit, Target, Rocket, FileText, MessageSquare, LineChart, PenTool, Tags, Globe, Wrench, Mic, Network, Search, HelpCircle, Smartphone, Edit3, MessageCircle, Zap, ArrowRight, BookOpen, Layers, LineChart as ChartOutlined } from 'lucide-react';
import ParticleSystem from '../components/ParticleSystem';
import TechStackMarquee from '../components/TechStackMarquee';
import heroBg from '../assets/images/hero-bg.png';
import MagneticEffect from '../components/MagneticEffect';
import TiltCard from '../components/TiltCard';

import caseStudy1 from '../assets/images/case-study-1.jpg';
import caseStudy2 from '../assets/images/case-study-2.jpg';
import caseStudy3 from '../assets/images/case-study-3.jpg';

const HeroSection = styled.section`
  background: var(--bg-primary);
  color: white;
  padding: 14rem 0 10rem;
  text-align: center;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: url(${heroBg});
    background-size: cover;
    background-position: center;
    opacity: 0.35;
    z-index: 0;
    mix-blend-mode: color-dodge;
  }

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 250px;
    background: linear-gradient(to top, var(--bg-primary), transparent);
    z-index: 1;
  }

  .container {
    position: relative;
    z-index: 2;
  }
`;

const HeroTitle = styled.h1`
  font-size: 4.5rem;
  font-weight: 800;
  margin-bottom: 2rem;
  line-height: 1.05;
  letter-spacing: -0.04em;

  .reveal-text {
    display: inline-block;
  }

  @media (max-width: 1024px) {
    font-size: 3.8rem;
  }

  @media (max-width: 768px) {
    font-size: 2.8rem;
  }

  @media (max-width: 480px) {
    font-size: 2rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.4rem;
  margin-bottom: 4rem;
  color: var(--text-secondary);
  max-width: 750px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.6;
  font-weight: 400;

  @media (max-width: 768px) {
    font-size: 1.2rem;
  }
`;

const CTAButtons = styled.div`
  display: flex;
  gap: 2rem;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
  margin-top: 2rem;
`;

const WhyChooseUsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 3rem;
  margin-top: 5rem;
  perspective: 1000px;
`;

const SolutionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 2rem;
  margin-top: 4rem;
  perspective: 1000px;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const FeaturesCard = styled.div`
  text-align: left;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid var(--border-subtle);
  border-radius: 20px;
  padding: 3rem;
  height: 100%;
  
  .icon-wrapper {
    width: 65px;
    height: 65px;
    border-radius: 16px;
    background: rgba(59, 130, 246, 0.1);
    color: var(--accent-primary);
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 2rem;
    border: 1px solid rgba(59, 130, 246, 0.2);
    box-shadow: 0 0 30px rgba(59, 130, 246, 0.2);
  }

  h3 {
    color: var(--text-primary);
    font-size: 1.6rem;
    font-weight: 600;
    margin-bottom: 1rem;
    letter-spacing: -0.02em;
  }

  p {
    color: var(--text-secondary);
    line-height: 1.7;
    font-size: 1.1rem;
  }
`;

const CapCard = styled.div`
  text-align: left;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  min-height: 280px;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid var(--border-subtle);
  border-radius: 20px;
  padding: 2.5rem 2rem;
  height: 100%;

  .icon-wrapper {
    width: 55px;
    height: 55px;
    border-radius: 14px;
    background: linear-gradient(135deg, rgba(59, 130, 246, 0.1), rgba(139, 92, 246, 0.1));
    color: var(--accent-primary);
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 1.5rem;
  }

  h3 {
    color: var(--text-primary);
    font-size: 1.3rem;
    font-weight: 600;
    margin-bottom: 1rem;
    line-height: 1.3;
    letter-spacing: -0.01em;
  }

  p {
    color: var(--text-secondary);
    line-height: 1.6;
    font-size: 1rem;
    margin-bottom: 2rem;
    flex-grow: 1;
  }

  .learn-more {
    color: var(--accent-primary);
    font-weight: 600;
    font-size: 1rem;
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    transition: all 0.3s ease;
    text-transform: uppercase;
    letter-spacing: 0.05em;

    &:hover {
      color: var(--accent-secondary);
      gap: 0.8rem;
    }
  }
`;

const CaseStudiesSection = styled.section`
  background-color: var(--bg-primary);
  padding: 8rem 0;
  border-top: 1px solid var(--border-subtle);
`;

const CaseStudyGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 3rem;
  margin-top: 4rem;
  perspective: 1200px;
`;

const CaseCard = styled.div`
  border-radius: 24px;
  overflow: hidden;
  background: var(--bg-secondary);
  border: 1px solid var(--border-subtle);
  height: 100%;
  display: flex;
  flex-direction: column;

  .image-container {
    height: 250px;
    width: 100%;
    overflow: hidden;
    position: relative;
    
    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      transition: transform 0.7s ease;
    }

    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      height: 100px;
      background: linear-gradient(to top, var(--bg-secondary), transparent);
    }
  }

  &:hover .image-container img {
    transform: scale(1.05);
  }

  .content {
    padding: 2.5rem;
    flex-grow: 1;
    display: flex;
    flex-direction: column;

    .industry-tag {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      color: var(--accent-primary);
      text-transform: uppercase;
      font-size: 0.85rem;
      font-weight: 700;
      letter-spacing: 0.1em;
      margin-bottom: 1rem;
    }

    h3 {
      font-size: 1.7rem;
      font-weight: 600;
      color: var(--text-primary);
      margin-bottom: 1rem;
      line-height: 1.3;
    }

    p {
      color: var(--text-secondary);
      line-height: 1.6;
      font-size: 1.05rem;
      margin-bottom: 2rem;
      flex-grow: 1;
    }

    .metric {
      display: inline-flex;
      align-items: baseline;
      gap: 0.5rem;
      background: rgba(59, 130, 246, 0.05);
      padding: 1rem 1.5rem;
      border-radius: 16px;
      border: 1px solid rgba(59, 130, 246, 0.15);
      width: 100%;

      .number {
        color: var(--text-primary);
        font-size: 2rem;
        font-weight: 700;
      }

      .label {
        color: var(--text-secondary);
        font-size: 0.95rem;
        text-transform: uppercase;
        letter-spacing: 0.05em;
      }
    }
  }
`;

const Home = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.25, 1, 0.5, 1] } }
  };

  // Text reveal animation logic
  const titleText = "Most AI systems are theater.";
  const titleText2 = "We build the ones that aren't.";
  const letters = Array.from(titleText);
  const letters2 = Array.from(titleText2);

  const letterVariants = {
    hidden: { opacity: 0, y: 50, rotateX: -90 },
    visible: { opacity: 1, y: 0, rotateX: 0, transition: { duration: 0.8, ease: [0.2, 0.65, 0.3, 0.9] } }
  };

  const whyChooseUsFeatures = [
    {
      icon: <BrainCircuit size={32} />,
      title: 'Deep Expertise',
      description: 'We bring together specialized AI researchers and enterprise engineers to architect robust solutions for complex organizational challenges.'
    },
    {
      icon: <Target size={32} />,
      title: 'Contextual Integration',
      description: 'We understand that generic tools fall short. Our approach ensures AI is meticulously integrated into your specific workflows and domain logic.'
    },
    {
      icon: <Rocket size={32} />,
      title: 'Scalable Execution',
      description: 'We prioritize tangible outcomes. From predictive models to automated document pipelines, we build resilient systems ready for production.'
    }
  ];

  const caseStudies = [
    {
      image: caseStudy1,
      industry: 'Quant Finance',
      icon: <ChartOutlined size={16} />,
      title: 'AI SDR for Wall Street',
      description: 'Automated prospecting, enrichment, and outreach for a premium financial brand targeting hedge funds. Integrated LangChain agents with Pydantic workflows for high-precision lead handling.',
      metricNumber: '+35%',
      metricLabel: 'Qualified Meetings'
    },
    {
      image: caseStudy2,
      industry: 'Compliance & Legal',
      icon: <Layers size={16} />,
      title: 'Knowledge GraphRAG Platform',
      description: 'Engineered an entity-linked graph over massive document stores for import/export compliance. Utilizing Neo4j and LangGraph to provide auditable citations with sub-second latency.',
      metricNumber: '+87%',
      metricLabel: 'Answer Precision'
    },
    {
      image: caseStudy3,
      industry: 'Market Research',
      icon: <BookOpen size={16} />,
      title: 'Multimodal Synthetic Surveys',
      description: 'Built a multi-agent framework to synthesize real-time respondent data for CPG and Tech brands. Delivering human-like insights at 10x lower cost than traditional survey panels.',
      metricNumber: '$300K+',
      metricLabel: 'Revenue Generated'
    }
  ];

  const ourSolutions = [
    { icon: <FileText size={24} />, title: 'Intelligent Document Processing', description: 'Transform unstructured data streams into highly structured, accessible information architectures.' },
    { icon: <MessageSquare size={24} />, title: 'Conversational Agents', description: 'Deploy context-aware virtual assistants that seamlessly guide users through complex inquiries.' },
    { icon: <LineChart size={24} />, title: 'Predictive Analytics', description: 'Empower your decision-making with robust statistical models that forecast critical industry trends.' },
    { icon: <Globe size={24} />, title: 'Cross-lingual NLP', description: 'Bridge communication gaps with advanced, culturally-nuanced machine translation and analysis.' },
  ];

  return (
    <>
      <HeroSection>
        <ParticleSystem particleCount={100} />
        <div className="container" style={{ maxWidth: '1200px' }}>
          <motion.div
            initial="hidden"
            animate="visible"
            variants={{
              visible: { transition: { staggerChildren: 0.04 } }
            }}
          >
            <HeroTitle>
              {letters.map((letter, index) => (
                <motion.span key={index} className="reveal-text" variants={letterVariants} style={{ display: 'inline-block' }}>
                  {letter === " " ? "\u00A0" : letter}
                </motion.span>
              ))}
              <br />
              <div className="gradient-text-accent" style={{ overflow: 'hidden', paddingBottom: '0.2em' }}>
                {letters2.map((letter, index) => (
                  <motion.span key={index} className="reveal-text" variants={letterVariants} style={{ display: 'inline-block' }}>
                    {letter === " " ? "\u00A0" : letter}
                  </motion.span>
                ))}
              </div>
            </HeroTitle>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1, duration: 0.8, ease: "easeOut" }}
            >
              <HeroSubtitle>
                We collaborate with ambitious organizations to architect intelligent infrastructure. Forget generic chatbots; we deliver bespoke data pipelines and prediction models that actually move the needle for your business.
              </HeroSubtitle>
              <CTAButtons>
                <MagneticEffect strength={0.4}>
                  <Link to="/contact" className="glow-btn" style={{ fontSize: '1.2rem', padding: '1.2rem 3rem', borderRadius: '30px' }}>
                    Collaborate With Us
                  </Link>
                </MagneticEffect>
                <MagneticEffect strength={0.4}>
                  <Link to="/solutions" className="btn-secondary" style={{ fontSize: '1.2rem', padding: '1.2rem 3rem', borderRadius: '30px' }}>
                    View Our Capabilities
                  </Link>
                </MagneticEffect>
              </CTAButtons>
            </motion.div>
          </motion.div>
        </div>
      </HeroSection>

      <TechStackMarquee />

      <section style={{ backgroundColor: 'var(--bg-secondary)', padding: '10rem 0' }}>
        <div className="container" style={{ maxWidth: '1200px' }}>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={containerVariants}
          >
            <div style={{ textAlign: 'left', marginBottom: '4rem', maxWidth: '800px' }}>
              <h2 className="section-subtitle">Our Approach</h2>
              <h3 className="section-title">We Deliver Insight. <br /><span className="gradient-text-accent">Not Just Software.</span></h3>
              <p className="section-description" style={{ fontSize: '1.2rem' }}>
                Our philosophy is simple: technology should amplify human capability.
                We combine deep theoretical research with pragmatic engineering to build intelligent workflows that are as reliable as they are sophisticated. We are the team you want.
              </p>
            </div>

            <WhyChooseUsGrid>
              {whyChooseUsFeatures.map((feature, index) => (
                <TiltCard key={index} variants={itemVariants}>
                  <FeaturesCard>
                    <div className="icon-wrapper">{feature.icon}</div>
                    <h3>{feature.title}</h3>
                    <p>{feature.description}</p>
                  </FeaturesCard>
                </TiltCard>
              ))}
            </WhyChooseUsGrid>
          </motion.div>
        </div>
      </section>

      <CaseStudiesSection>
        <div className="container" style={{ maxWidth: '1200px' }}>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={containerVariants}
          >
            <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
              <h3 className="section-title">Impact At <span className="gradient-text">Enterprise Scale</span></h3>
            </div>

            <CaseStudyGrid>
              {caseStudies.map((study, index) => (
                <TiltCard key={index} variants={itemVariants}>
                  <CaseCard>
                    <div className="image-container">
                      <img src={study.image} alt={study.title} />
                    </div>
                    <div className="content">
                      <span className="industry-tag">{study.icon} {study.industry}</span>
                      <h3>{study.title}</h3>
                      <p>{study.description}</p>
                      <div>
                        <div className="metric">
                          <span className="number">{study.metricNumber}</span>
                          <span className="label">{study.metricLabel}</span>
                        </div>
                      </div>
                    </div>
                  </CaseCard>
                </TiltCard>
              ))}
            </CaseStudyGrid>
          </motion.div>
        </div>
      </CaseStudiesSection>

      <section style={{ backgroundColor: 'var(--bg-secondary)', padding: '10rem 0' }}>
        <div className="container" style={{ maxWidth: '1200px' }}>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={containerVariants}
          >
            <div style={{ textAlign: 'left', marginBottom: '4rem', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: '2rem' }}>
              <div style={{ maxWidth: '600px' }}>
                <h2 className="section-subtitle">Capabilities</h2>
                <h3 className="section-title">The <span className="gradient-text">ContextJet</span> Portfolio</h3>
              </div>
              <MagneticEffect>
                <Link to="/solutions" className="glow-btn" style={{ padding: '1rem 2rem' }}>
                  Explore All Solutions <ArrowRight size={20} style={{ marginLeft: '10px' }} />
                </Link>
              </MagneticEffect>
            </div>

            <SolutionsGrid>
              {ourSolutions.map((solution, index) => (
                <TiltCard key={index} variants={itemVariants}>
                  <CapCard>
                    <div>
                      <div className="icon-wrapper">{solution.icon}</div>
                      <h3>{solution.title}</h3>
                      <p>{solution.description}</p>
                    </div>
                    <div>
                      <span className="learn-more">Details</span>
                    </div>
                  </CapCard>
                </TiltCard>
              ))}
            </SolutionsGrid>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default Home;